import * as React from 'react'
import { GatsbyBrowser } from 'gatsby'
import { RecoilRoot } from 'recoil'
import SETTINGS from './settings'
import './src/styles/global.css'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

let initialRouteHandled = false
let linkClickTrackingInitialized = false

const WEB_RESERVATION_TRACKING_URL = 'https://melmo-app.com/clinic/676d063ff63b542ac7c8fa65/reservation'

const sendAnalyticsEvent = (eventName: string, params: Record<string, string>) => {
  if (process.env.GATSBY_CLINIC_PREVIEW === 'true') return
  if (process.env.NODE_ENV !== 'production') return
  if (typeof window.gtag !== 'function') return

  window.gtag('event', eventName, {
    ...params,
    page_path: window.location.pathname + window.location.search + window.location.hash,
    page_location: window.location.href,
    page_title: document.title,
    transport_type: 'beacon',
  })
}

const getAnchorElement = (target: EventTarget | null): HTMLAnchorElement | null => {
  if (!(target instanceof Element)) return null
  return target.closest('a[href]')
}

const getLinkText = (anchor: HTMLAnchorElement): string => {
  return (anchor.innerText || anchor.getAttribute('aria-label') || anchor.title || '')
    .trim()
    .slice(0, 100)
}

const normalizePhoneNumber = (value: string): string => {
  return value.replace(/\D/g, '')
}

const handleLinkClick = (event: MouseEvent) => {
  const anchor = getAnchorElement(event.target)
  if (!anchor) return

  const href = anchor.getAttribute('href')
  if (!href) return

  const linkText = getLinkText(anchor)
  const trackedPhoneNumber = normalizePhoneNumber(SETTINGS.phoneNumber)

  if (href.startsWith('tel:') && normalizePhoneNumber(href) === trackedPhoneNumber) {
    sendAnalyticsEvent('click_tel', {
      event_category: 'conversion',
      link_url: href,
      link_text: linkText,
    })
    return
  }

  const linkUrl = new URL(href, window.location.href)
  const reservationUrl = new URL(SETTINGS.reservation)
  const reservationTrackingUrl = new URL(WEB_RESERVATION_TRACKING_URL)
  const isHttpLink = linkUrl.protocol === 'http:' || linkUrl.protocol === 'https:'
  const isReservationLink =
    linkUrl.href.startsWith(reservationUrl.href) ||
    linkUrl.href.startsWith(reservationTrackingUrl.href)

  if (isReservationLink) {
    sendAnalyticsEvent('click_web_reservation', {
      event_category: 'conversion',
      link_url: linkUrl.href,
      link_text: linkText,
    })
    return
  }

  if (isHttpLink && linkUrl.origin !== window.location.origin) {
    sendAnalyticsEvent('outbound_click', {
      event_category: 'engagement',
      link_url: linkUrl.href,
      link_domain: linkUrl.hostname,
      link_text: linkText,
    })
  }
}

export const wrapPageElement: GatsbyBrowser['wrapPageElement'] = ({ element }) => {
  
  return (
    <RecoilRoot>
      {element}
    </RecoilRoot>
  )
}

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
  if (process.env.GATSBY_CLINIC_PREVIEW === 'true') return
  if (linkClickTrackingInitialized) return
  linkClickTrackingInitialized = true

  document.addEventListener('click', handleLinkClick, true)
}

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({ location }) => {
  if (process.env.GATSBY_CLINIC_PREVIEW === 'true') return
  if (process.env.NODE_ENV !== 'production') return

  if (!initialRouteHandled) {
    initialRouteHandled = true
    return
  }

  if (typeof window.gtag !== 'function') return

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      window.gtag?.('event', 'page_view', {
        page_path: location.pathname + location.search + location.hash,
        page_location: window.location.href,
        page_title: document.title,
      })
    })
  })
}
